"use client";

import { MiniCartWhatsAppCapture } from "@/components/Cart/CartWhatsAppCapture";
import Image from "next/image";
import Link from "next/link";
import { Lock, ShoppingCart, Trash2, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useCart } from "@/hooks/useCart";
import { computeCartPaymentPreview } from "@/lib/commerce/cartPaymentPreview";
import { formatMinorBRL, moneyValueToMinor } from "@/lib/commerce/nativeCheckoutClient";
import { useCheckoutTransfer } from "@/hooks/useCheckoutTransfer";
import { FreeShippingBadge } from "@/components/Product/FreeShippingBadge";
import { OrderBumpTag } from "@/components/Cart/OrderBumpTag";
import { MiniCartFreeShippingBar, MiniCartSuggestions, useMiniCartUpsell } from "@/components/Cart/MiniCartUpsell";
import { QuantityStepper } from "@/components/UI/QuantityStepper";

export function MiniCart() {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const {
    cart,
    isLoading,
    isOpen: open,
    closeCart,
    error,
    pendingItemKey,
    removeItem,
    supportsCartShipping,
    unavailableItemKeys,
  } = useCart();
  const { checkoutError, isPreparingCheckout, prepareCheckout } =
    useCheckoutTransfer();
  const upsell = useMiniCartUpsell(open);
  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: cart?.currencyCode ?? "BRL",
  });
  // Prévia do Pix/boleto sobre o subtotal (só exibição; o checkout recalcula).
  const miniItemsMinor = cart ? moneyValueToMinor(cart.totals.items.value) : 0;
  const miniPreview = cart?.items.length && !cart.fees.some((fee) => /pix/i.test(fee.name))
    ? computeCartPaymentPreview({
        itemsMinor: miniItemsMinor,
        totalMinor: miniItemsMinor,
        hasCoupon: cart.coupons.some((coupon) => coupon.applied !== false),
      })
    : null;
  // Cupom aplicado (vem do link ?cupom= ou do carrinho): só a linha do
  // desconto -- no mini carrinho não há campo para digitar cupom.
  const appliedCoupon = cart?.nativeCoupon?.applied ? cart.nativeCoupon : null;
  const couponDiscountMinor = appliedCoupon ? Number(appliedCoupon.itemDiscountMinor) : 0;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeCart();
    }

    if (open) {
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
      if (open && triggerRef.current && document.contains(triggerRef.current)) {
        triggerRef.current.focus();
      }
    };
  }, [closeCart, open]);

  return (
    <>
      <button
        type="button"
        onClick={closeCart}
        aria-label="Fechar carrinho"
        aria-hidden={!open}
        inert={!open}
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          open
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
        }`}
      />

      <aside
        id="mini-cart-drawer"
        aria-label="Mini carrinho"
        aria-hidden={!open}
        inert={!open}
        className={`fixed inset-y-0 right-0 z-50 flex h-[100dvh] max-h-[100dvh] w-full max-w-[360px] flex-col overflow-hidden bg-white shadow-2xl transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="shrink-0 flex items-center justify-between border-b py-1 pl-5 pr-3">
          <h2 className="text-base font-semibold">
            Carrinho ({cart?.itemsCount ?? 0})
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={closeCart}
            className="inline-flex min-h-10 items-center gap-1 rounded-md px-2 text-sm font-medium transition-colors hover:bg-slate-100 active:bg-slate-200"
            aria-label="Fechar carrinho"
          >
            <X size={18} aria-hidden="true" />
            Fechar
          </button>
        </header>

        {cart?.items.length ? (
          <main className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-3 sm:space-y-5 sm:py-4">
            {cart.items.map((item) => (
              <article key={item.key} className="relative flex gap-2.5 pr-5 sm:gap-3">
                {item.image ? (
                  <Image
                    src={item.image.src}
                    alt={item.image.alt}
                    width={60}
                    height={60}
                    className="h-12 w-12 shrink-0 object-contain sm:h-[60px] sm:w-[60px]"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-[13px] font-medium leading-[18px] text-foreground sm:text-sm sm:leading-5">
                    {item.name}
                  </h3>
                  {unavailableItemKeys.includes(item.key) ? (
                    <p className="mt-1 text-xs font-medium text-red-700">
                      Indisponível no momento
                    </p>
                  ) : null}
                  {item.freeShipping ? <div className="mt-1"><FreeShippingBadge compact /></div> : null}
                  <OrderBumpTag discount={item.orderBumpDiscount} className="mt-1" />
                  {item.variation.length > 0 ? (
                    <dl className="mt-1 text-xs text-muted">
                      {item.variation.map((attribute) => (
                        <div key={`${attribute.attribute}-${attribute.value}`}>
                          <dt className="inline font-medium">
                            {attribute.label}:
                          </dt>{" "}
                          <dd className="inline">{attribute.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                  <QuantityStepper item={item} size="xs" />
                    <p className="text-xs text-slate-400">
                      {item.quantity} ×{" "}
                      <strong className="font-semibold text-heading">
                        {formatter.format(item.price)}
                      </strong>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void removeItem(item.key)}
                  disabled={pendingItemKey === item.key}
                  aria-label={`Remover ${item.name} do carrinho`}
                  title="Remover produto"
                  className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-red-50 hover:text-red-700 active:bg-red-100 active:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 disabled:cursor-wait disabled:opacity-50"
                >
                  {pendingItemKey === item.key ? (
                    <span className="h-3 w-3 animate-spin rounded-full border border-slate-300 border-t-primary" />
                  ) : (
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </article>
            ))}
            <MiniCartSuggestions state={upsell} />
            <MiniCartWhatsAppCapture />
          </main>
        ) : (
          <main className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto overscroll-contain px-8 text-center">
            <ShoppingCart
              size={72}
              strokeWidth={1.4}
              className="text-slate-300"
            />
            <h3 className="mt-6 text-lg font-semibold">
              Seu carrinho está vazio
            </h3>
            <p className="mt-3 text-sm leading-6 text-muted">
              Adicione produtos ao carrinho para visualizar o subtotal e
              finalizar sua compra.
            </p>
            <button
              type="button"
              onClick={closeCart}
              className="mt-8 w-full rounded-md bg-primary py-3 font-medium text-white transition-colors hover:bg-primary-hover active:bg-primary-hover"
            >
              CONTINUAR COMPRANDO
            </button>
          </main>
        )}

        {error || checkoutError ? (
          <p className="shrink-0 px-6 pb-3 text-sm text-red-700" role="status">
            {error || checkoutError}
          </p>
        ) : null}

        <footer className="shrink-0 border-t px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:pt-4">
          {cart?.items.length ? <MiniCartFreeShippingBar state={upsell} /> : null}
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-muted">Subtotal</span>
            <strong className="text-lg">
              {formatter.format(cart?.subtotal ?? 0)}
            </strong>
          </div>
          {appliedCoupon && (couponDiscountMinor > 0 || appliedCoupon.freeShipping) ? (
            <p className="-mt-1.5 mb-3 flex items-center justify-between text-sm text-emerald-700">
              <span>Cupom {appliedCoupon.code}</span>
              <strong>{couponDiscountMinor > 0 ? `− ${formatMinorBRL(couponDiscountMinor)}` : "Frete grátis"}</strong>
            </p>
          ) : null}
          {miniPreview ? (
            <p className="-mt-1.5 mb-3 text-xs text-slate-600">
              <strong className="text-emerald-700">{formatMinorBRL(miniPreview.pix.totalMinor)}</strong> no Pix ou{" "}
              <strong className="text-emerald-700">{formatMinorBRL(miniPreview.boleto.totalMinor)}</strong> no boleto
            </p>
          ) : null}
          {supportsCartShipping ? null : (
            <p className="-mt-1.5 mb-3 text-xs text-muted">
              O frete é calculado no checkout.
            </p>
          )}
          <Link
            href="/carrinho"
            onClick={closeCart}
            className="mb-2 inline-flex h-11 w-full items-center justify-center rounded-md border border-primary px-4 font-medium text-primary transition-colors hover:bg-slate-50 active:bg-slate-100"
          >
            Ver carrinho
          </Link>
          <button
            type="button"
            onClick={() => void prepareCheckout(undefined, closeCart)}
            disabled={
              !cart?.items.length ||
              isLoading ||
              Boolean(pendingItemKey) ||
              isPreparingCheckout
            }
            className="flex w-full items-center justify-center rounded-md bg-secondary py-3 font-medium text-white transition-colors hover:bg-secondary-hover active:bg-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:cursor-wait disabled:bg-slate-200 disabled:text-muted"
          >
            {isPreparingCheckout ? "PREPARANDO..." : "FINALIZAR COMPRA"}
          </button>
          {cart?.items.length ? (
            <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-muted">
              <Lock className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
              Pagamento seguro · Pix · Cartão em até 10x
            </p>
          ) : null}
        </footer>
      </aside>
    </>
  );
}
